// Product option modal — builds the cart payload for add / update.
//
// When editing an existing cart line, a selection block (variants, modifiers,
// combo, ingredients, service items) is only re-sent when it changed, compared
// by the same signatures the cart uses to merge identical lines.
import {
  getCartLineComboSignature,
  getCartLineIngredientSignature,
  getCartLineSelectionSignature,
  getCartLineServiceItemSignature,
} from '@core/utils/cartSelections';
import { firstNumber } from './normalize';
import { toPayloadGroupIdForSelection } from './prefill';
import { getSelectedAlternativeUomId } from './units';

// The synthetic 'single-variants' group only keys ungrouped variants inside the
// selection state; it is not a real API group, so it goes out as null.
const toPayloadGroupName = (groupId, groupName) =>
  groupId === 'single-variants' ? null : groupName;

const toVariantPayload = (option) => ({
  variant_id: option.variant_id ?? option.id,
  variant_name: option.variant_name ?? option.name,
  variant_group_id: toPayloadGroupIdForSelection(option.groupId),
  variant_group_name: toPayloadGroupName(option.groupId, option.groupName),
  price_delta: option.priceDelta,
});

const toModifierPayload = (option) => ({
  modifier_id: option.modifier_id ?? option.id,
  name: option.name ?? option.modifier_name,
  modifier_group_id: option.groupId,
  modifier_group_name: option.groupName,
  quantity: option.quantity,
  price_delta: option.priceDelta,
});

/** Item upsell picks → their own cart lines, linked to the offering item. */
export const buildItemUpsellLines = (product, upsells) =>
  upsells.map((u) => ({
    itemId: u.itemId,
    itemName: u.name,
    unitPrice: u.price,
    mrp: u.basePrice ?? u.price,
    quantity: u.quantity,
    uomId: u.uomId,
    upsellingItem: true,
    upsellItemId: u.upsellItemId,
    upsellingRefId: product.id,
  }));

/** True when the line is new, or `current` differs from what the line had. */
const changed = (initialLine, current, initial) => !initialLine?.lineId || current !== initial;

/**
 * @param {object} input
 * @param {object} input.product
 * @param {object|null} input.initialLine          cart line being edited (null when adding)
 * @param {object[]} input.selectedVariants
 * @param {object[]} input.selectedModifiers
 * @param {object[]} input.customModifiers         custom-modifier payload entries
 * @param {object[]} input.upsellItems             upsell payload entries
 * @param {object} input.upsellSelections
 * @param {object[]} input.comboSelections
 * @param {object[]} input.removedIngredients
 * @param {object[]} input.serviceItems
 * @param {object|null} input.alternativeUnit      chosen alternative-unit conversion
 * @param {boolean} input.isVariantSku
 * @param {object|null} input.selectedSku
 * @param {number} input.unitPrice
 * @param {number} input.mrp
 * @param {string} input.imageUrl
 * @param {number} input.quantity
 * @param {number} input.lineExtras                one-off extras (the cart's modExtra)
 */
export function buildCartLinePayload({
  product,
  initialLine,
  selectedVariants,
  selectedModifiers,
  customModifiers,
  upsellItems,
  upsellSelections,
  comboSelections,
  removedIngredients,
  serviceItems,
  alternativeUnit,
  isVariantSku,
  selectedSku,
  unitPrice,
  mrp,
  imageUrl,
  quantity,
  lineExtras,
}) {
  const variant = selectedVariants[0] ? toVariantPayload(selectedVariants[0]) : undefined;
  const variantPayload = selectedVariants.map(toVariantPayload);
  const modifiers = [
    ...selectedModifiers.map(toModifierPayload),
    ...customModifiers,
    ...upsellItems,
  ];

  const variantChanged = changed(
    initialLine,
    getCartLineSelectionSignature({ selectedVariants: variantPayload }),
    initialLine?.lineId &&
      getCartLineSelectionSignature({
        selectedVariants: initialLine.selectedVariants,
        variant: initialLine.variant,
      }),
  );
  const modifierChanged = changed(
    initialLine,
    getCartLineSelectionSignature({ selectedModifiers: modifiers }),
    initialLine?.lineId &&
      getCartLineSelectionSignature({
        selectedModifiers: initialLine.selectedModifiers,
        modifiers: initialLine.modifiers,
      }),
  );
  const comboChanged = changed(
    initialLine,
    getCartLineComboSignature({ comboSelections }),
    initialLine?.lineId &&
      getCartLineComboSignature({
        comboSelections: initialLine.comboSelections,
        selectedComboItems: initialLine.selectedComboItems,
      }),
  );
  const ingredientChanged = changed(
    initialLine,
    getCartLineIngredientSignature({ removedIngredients }),
    initialLine?.lineId &&
      getCartLineIngredientSignature({ removedIngredients: initialLine.removedIngredients }),
  );
  const serviceItemChanged = changed(
    initialLine,
    getCartLineServiceItemSignature({ serviceItems }),
    initialLine?.lineId &&
      getCartLineServiceItemSignature({
        serviceItems: initialLine.serviceItems,
        service_items: initialLine.service_items,
      }),
  );

  const unit = alternativeUnit ?? null;
  const alternativeUomId = getSelectedAlternativeUomId(unit);
  const baseUomId = product.uomId ?? product.uom_id;

  return {
    itemId: product.id,
    itemName: product.name,
    unitPrice,
    mrp,
    imageUrl,
    ...(isVariantSku && selectedSku?.id
      ? { itemVariantSkuId: selectedSku.id, skuLocationId: selectedSku.locationId }
      : {}),
    uom: product.uom,
    uomId: alternativeUomId ?? baseUomId,
    unit: unit?.unit_details?.name ?? unit?.unit ?? product.uom,
    unitId: unit?.unit ?? alternativeUomId ?? baseUomId,
    selectedUomId: alternativeUomId,
    conversionRate: firstNumber(unit?.conversion_rate, unit?.conversionRate),
    alternativeUnit: unit ? alternativeUomId : undefined,
    alternativeUnitConversion: unit,
    quantity,
    // Guest-cart totals track one additive "modExtra" bucket (see useCart.js
    // guestAdd/guestUpdate); combo, upsell and service-item pricing fold into it.
    modExtra: lineExtras,
    ...(variantChanged ? { variant, selectedVariants: variantPayload } : {}),
    ...(modifierChanged || modifiers.length > 0 ? { modifiers, selectedModifiers: modifiers } : {}),
    ...(comboChanged ? { comboSelections, selectedComboItems: comboSelections } : {}),
    ...(ingredientChanged ? { removedIngredients } : {}),
    ...(serviceItemChanged || serviceItems.length > 0 ? { serviceItems } : {}),
    ...(upsellItems.length > 0 ? { upsellItems, upsellSelections } : {}),
  };
}
